import React, { useState, useEffect } from 'react';
import Timeline from './Timeline';
import DateRangesReport from './DateRangesReport';
import './TimeSeriesAnalysis.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const TimeSeriesAnalysis = () => {
    const [data, setData] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [allCommunities, setAllCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [selectedCommunities, setSelectedCommunities] = useState([]);
    const [hoveredItem, setHoveredItem] = useState(null);

    useEffect(() => {
        fetchTimeSeriesData();
    }, [dateRange, selectedCommunities]);

    const fetchTimeSeriesData = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                start_date: dateRange.start,
                end_date: dateRange.end
            });

            if (selectedCommunities.length > 0) {
                selectedCommunities.forEach(communityId => {
                    params.append('community_id', communityId);
                });
            }

            const response = await apiFetch(`${API_BASE_URL}/monitoring/time-series-coverage?${params.toString()}`);

            if (!response.ok) throw new Error('Failed to fetch time series data');

            const result = await response.json();
            setData(result.data || []);
            setCommunities(result.communities || []);
            setAllCommunities(result.all_communities || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateDateRange = () => {
        const dates = [];
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            dates.push(new Date(d).toISOString().split('T')[0]);
        }
        return dates;
    };

    const getDayStatus = (communityId, date) => {
        const dayData = data.filter(item =>
            item.community_id === communityId && item.t === date
        );
        if (dayData.length === 0) return 'empty';
        if (dayData.length === 1) return 'single';
        return 'duplicate';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'empty': return 'status-empty';
            case 'single': return 'status-single';
            case 'duplicate': return 'status-duplicate';
            default: return 'status-empty';
        }
    };

    const getStatusTooltip = (communityId, date) => {
        const dayData = data.filter(item =>
            item.community_id === communityId && item.t === date
        );
        if (dayData.length === 0) return `Нет данных для ${date}`;
        const uniqueSessions = [...new Set(dayData.map(item => item.session_id))];
        return `
            Дата: ${date}
            Записей: ${dayData.length}
            Уникальных session_id: ${uniqueSessions.length}
            ${dayData.length > 1 ? '⚠️ Обнаружены дубли' : ''}
        `;
    };

    const dateRangeOptions = [
        { label: '7 дней', days: 7 },
        { label: '30 дней', days: 30 },
        { label: '90 дней', days: 90 },
        { label: '180 дней', days: 180 },
        { label: '365 дней', days: 365 }
    ];

    const handleDateRangeChange = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    };

    const dates = generateDateRange();

    return (
        <div className="tsa-container">
            <div className="tsa-header">
                <h2><i className="fas fa-clock"></i> Анализ заполненности временных рядов</h2>
                <p>Визуализация наличия данных по группам и датам</p>
            </div>

            <div className="tsa-controls">
                <div className="tsa-control-block">
                    <label>Временной диапазон</label>
                    <div className="tsa-button-group">
                        {dateRangeOptions.map(option => (
                            <button
                                key={option.days}
                                onClick={() => handleDateRangeChange(option.days)}
                                className={`tsa-btn ${((new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24)) === option.days ? 'active' : ''}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="tsa-control-block">
                    <label>Пользовательский диапазон</label>
                    <div className="tsa-date-inputs">
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                        />
                        <span>—</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                        />
                    </div>
                </div>

                <div className="tsa-control-block">
                    <label>Фильтр по группам ({allCommunities.length})</label>
                    <div className="tsa-community-list">
                        {allCommunities.map(community => (
                            <label key={community.id}>
                                <input
                                    type="checkbox"
                                    checked={selectedCommunities.includes(community.id)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedCommunities(prev => [...prev, community.id]);
                                        } else {
                                            setSelectedCommunities(prev => prev.filter(id => id !== community.id));
                                        }
                                    }}
                                />
                                <span title={community.name}>{community.name}</span>
                            </label>
                        ))}
                    </div>
                    {selectedCommunities.length > 0 && (
                        <button className="tsa-clear-filter" onClick={() => setSelectedCommunities([])}>
                            Очистить фильтр
                        </button>
                    )}
                </div>

                <button className="tsa-refresh" onClick={fetchTimeSeriesData}>
                    <i className="fas fa-sync-alt"></i> Обновить
                </button>
            </div>

            <div className="tsa-legend">
                <div><span className="legend-box status-empty"></span> Нет данных</div>
                <div><span className="legend-box status-single"></span> Одно значение</div>
                <div><span className="legend-box status-duplicate"></span> Дубли</div>
            </div>

            {loading && (
                <div className="tsa-loading">
                    <i className="fas fa-circle-notch fa-spin"></i> Загрузка данных...
                </div>
            )}

            {error && (
                <div className="tsa-error">
                    <i className="fas fa-exclamation-triangle"></i> {error}
                    <button onClick={fetchTimeSeriesData}>Повторить</button>
                </div>
            )}

            {!loading && !error && (
                <div className="tsa-content">
                    <Timeline
                        communities={communities}
                        dates={dates}
                        data={data}
                        getDayStatus={getDayStatus}
                        getStatusColor={getStatusColor}
                        getStatusTooltip={getStatusTooltip}
                        hoveredItem={hoveredItem}
                        setHoveredItem={setHoveredItem}
                    />

                    <div className="tsa-summary">
                        <div>
                            <h3>Всего групп</h3>
                            <p>{communities.length}</p>
                            {selectedCommunities.length > 0 && (
                                <small>Отфильтровано: {selectedCommunities.length}</small>
                            )}
                        </div>
                        <div>
                            <h3>Дней в диапазоне</h3>
                            <p>{dates.length}</p>
                        </div>
                        <div>
                            <h3>Записей с данными</h3>
                            <p>{data.length}</p>
                        </div>
                        <div>
                            <h3>Дней с дублями</h3>
                            <p className="error-text">
                                {(() => {
                                    const duplicateDates = new Set();
                                    communities.forEach(c => {
                                        dates.forEach(date => {
                                            if (getDayStatus(c.id, date) === 'duplicate') {
                                                duplicateDates.add(date);
                                            }
                                        });
                                    });
                                    return duplicateDates.size;
                                })()}
                            </p>
                        </div>
                    </div>

                    <div className="tsa-report">
                        <h3>Отчет по диапазонам дат</h3>
                        <DateRangesReport
                            communities={communities}
                            dates={dates}
                            getDayStatus={getDayStatus}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimeSeriesAnalysis;
